import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { PrivateMeetingDetailsResponseType, SpeakerListResponseType, SpeakerResponseType } from '../../../lib/api';
import { EventSettingsContextType, useEventSettings } from '../../../lib/context-providers/event-settings-context/event-settings-context';
import MeetingTimingBlock from '../MeetingTimingBlock';
import OrganizationsList from '../../../app/shared/OrganizationsList';
import { Button } from '../../../components/_base/Button';
import Icon from '../../../components/_base/Icon';
import './MeetingDetails.scss';
import useMeetingCalendar from '../../../hooks/use-meeting-calendar';
import OrganizationsListWithDetails from '../OrganizationListWithDetails/OrganizationListWithDetails';
import useEventToken from '../../../hooks/use-event-token';
import MeetingDetailsLinks from '../MeetingDetailsLinks/MeetingDetailsLinks';
import ConnectionWrapper from '../../passport/MeetingWrapper/ConnectionWrapper/ConnectionWrapper';
import { useTemplateProcessor } from '../../../hooks/use-template-processor';
import { getEventOffsetGMT, TimeZoneDisplayTypeLabels } from '../../../lib/helpers/dateHelper';
import { utcToZonedTime } from 'date-fns-tz';
import { usePathPrefix } from '../../../hooks/use-path-prefix';
import { getBookAMeetingPath, isBookAMeetingPath } from '../../../lib/helpers/urlHelper';
import { CALENDAR_INVITES, ModalMode, PassportNonce } from '../../../lib/constants';
import SafeHTMLRender from '../SafeHTMLRender';
import SubscriptionCalendarButtons from '../SubscribeCalendarButton';
import { useCurrentUserInfo } from '../../../hooks/api/protected/use-current-user-info';
import useEventCalendar from '../../../hooks/use-event-calendar';
import { sortOrganizationsByPriorityInEvent } from '../../../lib/helpers/meetingHelper';
import BookMeetingModal from '../../passport/BookAMeetingWrapper/BookMeetingModal';
import BookingResultModal from '../../passport/BookAMeetingWrapper/BookingResultModal';
import { RegistrationResult, RegistrationResultType } from '../../../hooks/use-booking-subscription';
import isEnabledRoute from '../../../hooks/use-enabled-route';
import { IAuthContext, useAuth } from '../../../lib/context-providers/auth-context';
import MeetingDetailsNavigation from './Navigation/MeetingDetailsNavigation';

export interface IProps {
  meetingDetails: PrivateMeetingDetailsResponseType;
  isConnectBtnDisabled: boolean;
  timeZone: string;
  showTopNavBtns?: boolean;
  agendaLabel?: string;
  eventName?: string;
  eventId: string;
  showAddToCalendarBtn?: boolean;
  eventCustomTitle?: string
  className?: string;
  singleMeetingPath?: string;
  meetingLinksLabel?: string;
  calendarInvite: CALENDAR_INVITES
}

export const PREVENTED_DETAILS_TEXT = 'Connection Details will be provided before this meeting begins.';

const MeetingDetails: React.FunctionComponent<IProps> = ({
  isConnectBtnDisabled = true,
  ...props
}: IProps) => {
  const data = useEventSettings() as EventSettingsContextType;
  const pathPrefix = usePathPrefix();
  const startDateTime = utcToZonedTime(data.startTime, data.timeZone);
  const templateProcessor = useTemplateProcessor();
  const calendarButtonEnabled = data.calendarButtonOnMeetingDetailsPageEnabled ?? true;
  const timeZoneLabel = getEventOffsetGMT(
    data.timeZoneDisplayType as TimeZoneDisplayTypeLabels,
    data.customTimeZoneDisplay as string,
    startDateTime,
    data.timeZone
  );
  const getTransformedTimeZone = () => {
    return data.timeZoneDisplayType === TimeZoneDisplayTypeLabels.Custom ? templateProcessor(timeZoneLabel) : timeZoneLabel;
  };
  const transformedTimeZone = data.showEventTimeZone ? getTransformedTimeZone() : '';
  const eventToken = useEventToken(Number(props.eventId));
  const history = useHistory();
  const { fetchOutlookCalendarForMeeting, fetchGoogleCalendarForMeeting } = useMeetingCalendar();
  const { unSubscribeToCalendarEvent } = useEventCalendar();
  const { data: currentUserInfo } = useCurrentUserInfo();
  const auth = useAuth() as IAuthContext;
  const [attendeeSubscribed, setAttendeeSubscribed] = useState(props.meetingDetails.attendeeSubscribedToMeeting);
  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false);
  const [showCancelSuccessModal, setShowCancelSuccessModal] = useState(false);
  const [cancelResult, setCancelResult] = useState<RegistrationResult>(null);

  const landingPageRoute = isEnabledRoute(data?.landingPage);

  useEffect(() => {
    setAttendeeSubscribed(props.meetingDetails.attendeeSubscribedToMeeting);
  }, [props.meetingDetails.attendeeSubscribedToMeeting]);
  const updateAttendeeSubscribed = (subscribed: boolean): void => {
    setAttendeeSubscribed(subscribed);
  };

  const getBookAMeetingRoute = (): string | null => {
    const historyState = history.location.state;
    if (
      typeof historyState === 'string' &&
      isBookAMeetingPath(historyState, data, pathPrefix, auth.isAuthenticated)
    ) {
      return historyState;
    }

    return getBookAMeetingPath(data, pathPrefix, auth.isAuthenticated);
  };

  const redirectToBookAMeetingOrLanding = (result?: RegistrationResult) => {
    const bookAMeetingRoute = getBookAMeetingRoute();
    const targetRoute = bookAMeetingRoute || landingPageRoute;
    history.push(targetRoute, { cancellationResult: result });
  };

  const handleCancelBookingClick = () => {
    setShowCancelConfirmationModal(true);
  };

  const handleCancelConfirmation = (result: RegistrationResult) => {
    setShowCancelConfirmationModal(false);
    setCancelResult(result);
    
    if (result === RegistrationResultType.REMOVED) {
      redirectToBookAMeetingOrLanding(result);
    } else {
      setShowCancelSuccessModal(true);
    }
  };

  const handleCancelSuccessModalClose = () => {
    setShowCancelSuccessModal(false);
  };
  const getOrganizationName = (): string => {
    return (props.meetingDetails.presenterOrganizations?.length)
      ? props.meetingDetails.presenterOrganizations.map(o => o.name).join(', ')
      : '';
  };

  const { meetingDetails: details, singleMeetingPath, meetingLinksLabel } = props;

  const attendeesWithDetails: SpeakerListResponseType = [];
  /* eslint-disable @typescript-eslint/no-unused-expressions */
  [
    ...(props.meetingDetails.hostOrganizations ?? []),
    ...(props.meetingDetails.participantOrganizations ?? []),
    ...(props.meetingDetails.presenterOrganizations ?? [])
  ].forEach(organization => {
    organization.attendees?.map(attendee => {
      if (attendee.biography || attendee.profileImageUrl) {
        const attendeeOrganization = attendeesWithDetails.find(el => el.organizationId === organization.organizationId);
        attendeeOrganization ? attendeeOrganization.speakers?.push(attendee as SpeakerResponseType) : attendeesWithDetails.push({ ...organization, speakers: [attendee as SpeakerResponseType]});
      }
      return attendee;
    });
    return organization;
  });

  const sortedOrganizationsByPriorityInEvent: SpeakerListResponseType = sortOrganizationsByPriorityInEvent(attendeesWithDetails);

  return (
    <div className='md:w-3/4 w-100'>
      <div className={`shadow-gray bg-white mx-auto md:px-8 px-0 p-8 mt-12 ${props.className ?? ''}`}>
        <MeetingDetailsNavigation
          showTopNavBtns={props.showTopNavBtns}
          agendaLabel={props.agendaLabel}
        />

        <div className='block sm:flex flex-none sm:flex-row items-start items-stretch max-h-full min-h-full'>
          <div className='flex-equal sm:flex-1 px-6 md:px-0 py-6 sm:pb-0 overflow-visible md:w-1/2'>
            <div className='pt-2 xs:pt-8 pb-5 sm:pl-8 sm:pr-12'>
              <MeetingTimingBlock 
                transformedTimeZone={transformedTimeZone} 
                meetingDetails={props.meetingDetails} 
                timeZone={props.timeZone}
                localTimeFontSize='font-size-12px'
              />
              {props.meetingDetails.type && <p className='text-sm'>
                <span className='text-primary text-xs font-bold'>Meeting type:&nbsp;</span>
                <span className='text-xs'>{props.meetingDetails.type}</span>
              </p>}
              {(props.meetingDetails.cancelBooking) && (
                <div className='cancel-booking-btn--wrapper sm:pl-8 mt-4'>
                  <Button
                    onClick={handleCancelBookingClick}
                    name="cancel-booking"
                    className="cancel-booking-btn flex items-center"
                  >
                    <Icon.Remove width="18" height="18" viewBox="5 5 14 14" />
                    <span>Cancel Booking</span>
                  </Button>
                </div>
              )}
            </div>
            {(props.meetingDetails.topic || props.meetingDetails.notes) && <div className='pt-3 pb-5 px-0 sm:pl-8 sm:pr-12'>
              <h2 className='text-primary meeting-topic font-semibold font-size-18px pb-1'>{props.meetingDetails.topic}</h2>
              <SafeHTMLRender nonce={PassportNonce.RICH_TEXT} html={props.meetingDetails.notes} className='text-sm whitespace-pre-wrap break-words'/>
            </div>}
            {props.meetingDetails.heroImageUrl &&
              <div className='mb-5 sm:ml-8 sm:mr-12'>
                <img src={props.meetingDetails.heroImageUrl} alt='hero'/>
              </div>}
            {props.showAddToCalendarBtn && calendarButtonEnabled &&
              <div className='add-to-calendar-btn--wrapper sm:pl-8'>
                <SubscriptionCalendarButtons
                  eventId={props.eventId.toString()}
                  name={getOrganizationName()}
                  attendeeSubscribed={attendeeSubscribed}
                  calendarInvite={props.calendarInvite}
                  data={currentUserInfo}
                  className={'editable-welcome--subscribe-title'}
                  eventTitleForPassport={props.meetingDetails.topic}
                  downloadIcsFile={() => fetchOutlookCalendarForMeeting(
                    props.eventId ?? '',
                    details.meetingId,
                    `${props.eventCustomTitle || props.eventName}.ics`,
                    eventToken,
                    singleMeetingPath
                  )}
                  openGoogleCalendar={() => fetchGoogleCalendarForMeeting(
                    props.eventId ?? '',
                    details.meetingId,
                    eventToken,
                    singleMeetingPath
                  )}
                  unSubscribeToCalendar={(resgistrationId: string) => unSubscribeToCalendarEvent(resgistrationId)}
                  subscribeButtonLabel='Add to Calendar'
                  subscribedButtonLabel='Added to Calendar'
                  isSubscribeToEvent
                  updateSubscribe={updateAttendeeSubscribed}
                  meetingId={details.meetingId}
                  displayFullLabel={true}
                  meetingEndDateTime={props.meetingDetails.endDateTime}
                />
              </div>
            }
            {props.meetingDetails.presenterOrganizations &&
              <OrganizationsList
                blockName=''
                organizations={props.meetingDetails.presenterOrganizations}
              />}
            {props.meetingDetails.participantOrganizations &&
              <OrganizationsList
                blockName={props.meetingDetails.presenterOrganizations ? 'Attendees:' : ''}
                organizations={props.meetingDetails.participantOrganizations}
              />}
            {props.meetingDetails.hostOrganizations &&
              <OrganizationsList
                blockName='Hosted by:'
                organizations={props.meetingDetails.hostOrganizations}
              />}
            {props.meetingDetails.links.length > 0 &&
              <MeetingDetailsLinks
                links={props.meetingDetails.links}
                meetingLinksLabel={meetingLinksLabel}
              />}
            {sortedOrganizationsByPriorityInEvent.length > 0 &&
              <OrganizationsListWithDetails
                organizations={sortedOrganizationsByPriorityInEvent}
              />}
          </div>
          <ConnectionWrapper
            meetingDetails={props.meetingDetails}
            isConnectBtnDisabled={isConnectBtnDisabled}
            eventId={props.eventId}
          />
        </div>
        {!props.meetingDetails && <p className='text-center pt-4 pb-8'>No meeting details yet</p>}
      </div>
      {showCancelConfirmationModal && (
        <BookMeetingModal
          isOpen={showCancelConfirmationModal}
          onClose={() => setShowCancelConfirmationModal(false)}
          onConfirm={handleCancelConfirmation}
          slot={null}
          speakers={[]}
          eventTimeZone={props.timeZone}
          eventId={props.eventId}
          meetingId={details.meetingId}
          mode={ModalMode.CANCEL}
          meetingDetails={props.meetingDetails}
          transformedTimeZone={transformedTimeZone}
        />
      )}
      {showCancelSuccessModal && (
        <BookingResultModal
          isOpen={showCancelSuccessModal}
          onClose={handleCancelSuccessModalClose}
          slot={null}
          speakers={[]}
          eventTimeZone={props.timeZone}
          showLocalTime={false}
          result={cancelResult}
          isCancellation={true}
          transformedTimeZone={transformedTimeZone}
        />
      )}
    </div>
  );
};

export default MeetingDetails;
